<template>
  <div class="print-page">
    <div class="no-print controls">
      <button @click="printNow()" class="print-btn">
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <polyline points="6 9 6 2 18 2 18 9" />
          <path
            d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"
          />
          <rect x="6" y="14" width="12" height="8" />
        </svg>
        Print
      </button>
      <router-link to="/viewparents" class="back-btn">← Back</router-link>
    </div>

    <div v-if="loading" class="no-data">
      <b-spinner type="grow" small></b-spinner> Loading parents...
    </div>

    <div v-else-if="!hasData" class="no-data">
      No parents found.
    </div>

    <div v-else>
      <h4
        style="text-align: center; text-transform: uppercase; border-bottom: 2px solid #000;"
      >
        {{ heading }}
      </h4>

      <table class="parents-table">
        <thead>
          <tr>
            <th class="number-column">#</th>
            <th>Name</th>
            <th class="gender-column">Gender</th>
            <th class="telephone-column">Telephone</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(parent, index) in dedupedParents" :key="parent.id">
            <td class="number-column">{{ index + 1 }}</td>
            <td class="parent-name">{{ parent.name }}</td>
            <td>{{ parent.gender }}</td>
            <td>{{ parent.telephone }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "PrintParents",
  data() {
    return {
      parents: [],
      loading: true,
      year: "",
      class_name: "",
    };
  },
  computed: {
    hasData() {
      return this.dedupedParents.length > 0;
    },
    // One record per parent, even if they have multiple children,
    // sorted alphabetically by name (case-insensitive).
    dedupedParents() {
      const seen = new Map();
      this.parents.forEach((item) => {
        const p = item.parent || {};
        if (p.id == null) return;
        if (!seen.has(p.id)) {
          seen.set(p.id, {
            id: p.id,
            name: (p.name || "").trim(),
            telephone: p.telephone || "",
            gender: p.gender || "",
          });
        }
      });
      return Array.from(seen.values()).sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
      );
    },
    heading() {
      if (this.class_name) {
        return `${this.class_name} Parents / Guardians - ${this.year}`;
      }
      return "All Parents / Guardians";
    },
  },
  async mounted() {
    const { year, class: classId, class_name: className } = this.$route.query;
    this.year = year || "";
    this.class_name = className || "";

    // No year → still allow printing everything
    try {
      let response;
      if (classId) {
        const formData = new FormData();
        formData.append("year", year || "");
        formData.append("schoolclass", classId);
        response = await axios.post("/filterparents", formData);
      } else {
        response = await axios.get("/parents");
      }
      if (response.status === 200) {
        this.parents = Array.isArray(response.data.parents)
          ? response.data.parents
          : [];
      }
    } catch (e) {
      // silently fail — same pattern as PrintFeesDefaulters
    } finally {
      this.loading = false;
    }
  },
  methods: {
    printNow() {
      window.print();
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=EB+Garamond:wght@400;600&family=DM+Sans:wght@400;500&display=swap");

* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

.print-page {
  font-family: "DM Sans", sans-serif;
  max-width: 720px;
  margin: 0 auto;
  padding: 40px 32px;
  background: #fff;
  color: #111;
  min-height: 100vh;
}

.no-print.controls {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 36px;
}
.print-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 20px;
  background: #111;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-family: "DM Sans", sans-serif;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
}
.back-btn {
  font-size: 14px;
  color: #666;
  text-decoration: none;
}
.back-btn:hover {
  color: #111;
}

.no-data {
  text-align: center;
  padding: 80px 0;
  color: #999;
  font-size: 15px;
}

.parents-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.parents-table th,
.parents-table td {
  padding: 9px 10px;
  border-bottom: 1px solid #e5e7e9;
  text-align: left;
  vertical-align: top;
}
.parents-table th {
  background: #f1f3f4;
  color: #333;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
}
.parents-table .number-column {
  width: 38px;
  color: #777;
  text-align: right;
}
.parents-table .gender-column {
  width: 110px;
}
.parents-table .telephone-column {
  width: 160px;
}
.parents-table .parent-name {
  font-weight: 500;
}

@media print {
  .no-print {
    display: none !important;
  }
  .print-page {
    padding: 20px;
  }
  .parents-table thead {
    display: table-header-group;
  }
  .parents-table tr {
    page-break-inside: avoid;
  }
}
</style>
